import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2, Loader2, TriangleAlert, Upload } from "lucide-react";
import {
  isValidLogoTicker,
  logoBlobName,
  type UploadAssetLogoResponse,
} from "../../../../../shared/index";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

const MAX_SVG_BYTES = 1024 * 1024;

function describeError(e: unknown): string {
  return e instanceof ApiError ? e.message : "Something went wrong";
}

function looksLikeSvg(text: string): boolean {
  return /<svg[\s>]/i.test(text);
}

export default function AssetsLogoUploadPage() {
  const [ticker, setTicker] = useState("");
  const [svg, setSvg] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const [tickerError, setTickerError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [result, setResult] = useState<UploadAssetLogoResponse | null>(null);

  // Proactive "already exists" feedback as the user types a valid ticker.
  const [existsWarning, setExistsWarning] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const tickerValid = isValidLogoTicker(ticker);

  // Debounced existence check.
  useEffect(() => {
    setExistsWarning(null);
    if (!tickerValid) return;
    const t = ticker.trim().toUpperCase();
    const timeout = setTimeout(() => {
      api.assetsLogo
        .exists(t)
        .then((res) => {
          if (res.exists) {
            setExistsWarning(
              `${t}.svg already exists on the CDN — uploading is blocked (no overwrite).`
            );
          }
        })
        .catch(() => {
          /* non-fatal: the upload itself is the authoritative guard */
        });
    }, 400);
    return () => clearTimeout(timeout);
  }, [ticker, tickerValid]);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFileError(null);
    setResult(null);
    setBanner(null);
    const file = e.target.files?.[0];
    if (!file) {
      setSvg(null);
      setFileName(null);
      return;
    }
    const isSvg =
      file.type === "image/svg+xml" || file.name.toLowerCase().endsWith(".svg");
    if (!isSvg) {
      setSvg(null);
      setFileName(null);
      setFileError("Please select an .svg file");
      return;
    }
    if (file.size > MAX_SVG_BYTES) {
      setSvg(null);
      setFileName(null);
      setFileError("SVG is too large (max 1 MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? "");
      if (!looksLikeSvg(text)) {
        setSvg(null);
        setFileName(null);
        setFileError("File does not look like a valid SVG");
        return;
      }
      setSvg(text);
      setFileName(file.name);
      // Convenience: prefill the ticker from the filename if empty.
      if (!ticker.trim()) {
        const base = file.name.replace(/\.svg$/i, "").toUpperCase();
        if (isValidLogoTicker(base)) setTicker(base);
      }
    };
    reader.onerror = () => setFileError("Could not read the file");
    reader.readAsText(file);
  };

  const uploadMutation = useMutation({
    mutationFn: api.assetsLogo.upload,
    onSuccess: (data) => {
      setResult(data);
      setBanner(null);
      setTickerError(null);
      // Reset the picker so an accidental re-submit can't repeat.
      setSvg(null);
      setFileName(null);
      setExistsWarning(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    onError: (e) => {
      setResult(null);
      if (e instanceof ApiError && e.status === 409) {
        setBanner(describeError(e));
        setTickerError("A logo for this ticker already exists");
      } else {
        setBanner(describeError(e));
      }
    },
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setResult(null);
    setBanner(null);
    setTickerError(null);

    const t = ticker.trim().toUpperCase();
    if (!isValidLogoTicker(t)) {
      setTickerError(
        "Ticker must be uppercase letters/digits (colon, dot, hyphen allowed), e.g. AAPL or GB:AAPL"
      );
      return;
    }
    if (!svg) {
      setFileError("Please select an .svg file");
      return;
    }
    uploadMutation.mutate({ ticker: t, svg });
  };

  const errorInputClass = "border-destructive focus-visible:ring-destructive";
  const previewSrc = svg
    ? `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`
    : null;
  const targetBlob = tickerValid
    ? logoBlobName(ticker.trim().toUpperCase())
    : null;

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <Link
          to="/dashboard/assets-logo"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Assets Logo
        </Link>
        <h1 className="text-2xl font-bold">Upload Logo</h1>
        <p className="text-sm text-muted-foreground">
          Upload a company logo SVG. Files are named{" "}
          <code className="rounded bg-muted px-1">TICKER.svg</code> and are never
          overwritten.
        </p>
      </div>

      {banner && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{banner}</span>
        </div>
      )}

      {existsWarning && !banner && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-500/50 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-400">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{existsWarning}</span>
        </div>
      )}

      {result && (
        <div className="rounded-lg border border-emerald-500/50 bg-emerald-500/10 px-4 py-3 text-sm">
          <div className="flex items-center gap-2 font-medium text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 className="h-4 w-4" />
            {result.ticker}.svg uploaded
          </div>
          <a
            href={result.url}
            target="_blank"
            rel="noreferrer"
            className="mt-1 block break-all text-primary underline"
          >
            {result.url}
          </a>
        </div>
      )}

      <form onSubmit={handleSubmit} className="max-w-md space-y-5">
        <div className="space-y-2">
          <Label htmlFor="ticker">Ticker</Label>
          <Input
            id="ticker"
            value={ticker}
            onChange={(e) => {
              setTicker(e.target.value.toUpperCase());
              setTickerError(null);
            }}
            placeholder="AAPL or GB:AAPL"
            autoCapitalize="characters"
            className={cn((tickerError || existsWarning) && errorInputClass)}
            aria-invalid={Boolean(tickerError)}
          />
          {tickerError ? (
            <p className="text-xs text-destructive">{tickerError}</p>
          ) : targetBlob ? (
            <p className="text-xs text-muted-foreground">
              Uploads to <code className="rounded bg-muted px-1">{targetBlob}</code>
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Uppercase; a colon exchange prefix is allowed (e.g. GB:AAPL).
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="svg">SVG file</Label>
          <Input
            id="svg"
            ref={fileInputRef}
            type="file"
            accept=".svg,image/svg+xml"
            onChange={handleFileChange}
            className={cn("cursor-pointer", fileError && errorInputClass)}
            aria-invalid={Boolean(fileError)}
          />
          {fileError ? (
            <p className="text-xs text-destructive">{fileError}</p>
          ) : fileName ? (
            <p className="text-xs text-muted-foreground">Selected: {fileName}</p>
          ) : (
            <p className="text-xs text-muted-foreground">SVG only, max 1 MB.</p>
          )}
          {previewSrc && (
            <div className="mt-2 flex h-24 w-24 items-center justify-center rounded-lg border bg-card p-2">
              <img
                src={previewSrc}
                alt="Logo preview"
                className="max-h-full max-w-full"
              />
            </div>
          )}
        </div>

        <div className="pt-2">
          <Button
            type="submit"
            disabled={uploadMutation.isPending || !tickerValid || !svg}
          >
            {uploadMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            Upload logo
          </Button>
        </div>
      </form>
    </div>
  );
}
